import { DateTime } from 'luxon';
import { createNumberMask } from 'text-mask-addons';

const formatter = new Intl.NumberFormat('en', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatterInfo = new Intl.NumberFormat('en', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatterPercentages = new Intl.NumberFormat('en', {
  maximumSignificantDigits: 2,
});

export const stripWhitespace = (value: string | undefined): string => {
  if (value && typeof value === 'string' && value !== '') {
    return value.replace(/ /g, '');
  } else {
    return '';
  }
};

export const formatMonthlyAmount = (value) =>
  `R ${formatCurrencyAmount(!value ? 0 : value)} / month`;

export const formatPercentageSimple = (amount) => {
  if (typeof amount === 'string') {
    amount = parseFloat(amount) || amount;
  }
  const isInteger =
    amount === null || ((!!amount || amount === 0) && Math.floor(amount) === amount);
  let formatted;
  if (isInteger) {
    formatted = formatterPercentages.format(amount);
  } else {
    formatted = formatterPercentages.format(amount);
  }
  return formatted !== 'NaN' ? formatted : amount;
};

/* Technical format e.g. for contract reviewing
Trailing zeros and comma as thousands grouping separator */
export const formatCurrencyAmount = (amount) => {
  if (typeof amount === 'string') {
    amount = parseFloat(amount) || amount;
  }
  let formatted = formatter.format(amount);
  return formatted !== 'NaN' ? formatted : amount;
};

/* Format for advertising and conversational/informational purposes
No trailing zeros and use space as thousands grouping separator */
export const formatCurrencyAmountSimple = (amount) => {
  if (typeof amount === 'string') {
    amount = parseFloat(amount) || amount;
  }
  const isInteger =
    amount === null || ((!!amount || amount === 0) && Math.floor(amount) === amount);
  let formatted;
  if (isInteger) {
    formatted = formatterInfo.format(amount);
  } else {
    formatted = formatter.format(amount);
  }
  return formatted !== 'NaN' ? formatted.replace(/,(?=\d*(\.|,|$))/g, ' ') : amount;
};

/* Mask format for South African mobile numbers.
   Does not allow spaces or leading zero */
export const phoneNumberMask = [
  '+',
  '2',
  '7',
  /[1-9]/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

export const phoneNumberMaskSpaced = [
  '+',
  '2',
  '7',
  ' ',
  /[1-9]/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

export const idMask = [
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
];

export const spaceBy4Mask = (value: string): (RegExp | string)[] => {
  value = stripWhitespace(value.replace('_', ''));

  const arr = new Array<RegExp | string>(value.length)
    .fill(/(\w|\d)/)
    .reduce<(RegExp | string)[]>((accum, curr, index) => {
      if (index !== 0 && index % 4 === 0) {
        accum.push(' ');
      }

      accum.push(curr);

      return accum;
    }, [])
    .reverse();

  return arr;
};

export const businessRegistrationMask = [
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  '/',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  '/',
  /\d/,
  /\d/,
];

export const serialNumberMaskSpaced = [
  /[KkNn]/,
  /[385]/,
  /[Rr86]/,
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  ' ',
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
  ' ',
  /[A-Za-z0-9]/,
  /[A-Za-z0-9]/,
];

export const trustRegistrationMask = [
  'I',
  'T',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  '/',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

//Text formatting
export const formatPhoneNumberText = (text) => {
  if (typeof text === 'string') {
    var phone = text.replace(/(\d{2})(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');
    return phone;
  } else {
    return text;
  }
};

/**
 * Format the ID number to display in a user friendly format.
 * @param {string} idNumber ID number to format
 */
export const formatIDNumberText = (text: string) => {
  if (typeof text === 'string') {
    var IDFormatted = text.replace(/(\d{6})(\d{4})(\d{3})/, '$1 $2 $3');
    return IDFormatted;
  } else {
    return text;
  }
};

export const toLowerCaseIfTruthy = (text) => {
  return text ? text.toLowerCase() : text;
};

export const formatStartDate = (date) => {
  return date ? DateTime.fromJSDate(date).toFormat('dd LLL HH:mm') : '';
};

export const formatEndDate = (date) => {
  return date ? DateTime.fromJSDate(date).toFormat('dd LLL yy HH:mm') : '';
};

export const removeNonNumericCharacters = (text) => {
  return text.replace(/[^0-9]/g, '');
};

export const returnValueIfTruthyElseEmptyString = (value) => {
  return value ? value : '';
};

export const toTitleCase = (text) =>
  text.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase());

// Regex: ^[A-Z][0-9]{8}$
export const refugeeRegex = /^[A-Z][0-9]{8}$/;
export const refugeeMask = [/[A-Za-z]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];

export const asylumMask = (value: string): (RegExp | string)[] => {
  // Regex: ^[A-Za-z0-9]{0,11}$
  value = stripWhitespace(value.replace('_', ''));

  const length = value.length < 25 ? value.length : 25;
  let arr = new Array<RegExp | string>(length).fill(/[A-Za-z0-9]/);
  return arr;
};

export const asylumRegex = /^[A-Za-z0-9]{0,11}$/;
export const passportRegex = /^[A-Za-z]{1}[A-Za-z0-9]{3,14}$/;
export const passportMask = (value: string): (RegExp | string)[] => {
  value = stripWhitespace(value.replace('_', ''));

  const length = value.length < 14 ? value.length : 14;
  let arr = new Array<RegExp | string>(length).fill(/[A-Za-z0-9]/);
  arr.push(/[A-Za-z]/);
  return arr.reverse();
};

export const turnoverMask = createNumberMask({
  prefix: '',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ' ',
  allowDecimal: false,
  decimalSymbol: '.',
  decimalLimit: 0,
  integerLimit: 9,
  allowNegative: false,
  allowLeadingZeroes: false,
  inputMode: 'numeric',
});

export const centInputMask = createNumberMask({
  prefix: '',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ' ',
  allowDecimal: true,
  decimalSymbol: '.',
  decimalLimit: 2,
  integerLimit: 9,
  allowNegative: false,
  allowLeadingZeroes: false,
  inputMode: 'numeric',
});

export function stripRandSymbolPrefix(text: string): string {
  return text ? text.replace(/R/g, '') : text;
}

export const parseCurrencyAmount = (amount: string): number => {
  return Number.parseInt(stripWhitespace(stripRandSymbolPrefix(amount)) || '0');
};

export const serialNumberPattern =
  /(K3R|N86|N5)[A-NP-Za-np-z0-9]{3}(?:\s?[A-Za-z0-9]{3})(?:\s?[A-Za-z0-9]{2})/i;

export const formatMobileNumber = (text: string): string => {
  if (!text) return 'Not a valid mobile number';
  let cleanText = removeNonNumericCharacters(stripWhitespace(text));
  if (cleanText.startsWith('27')) {
    cleanText = cleanText.replace('27', '0');
  }
  if (cleanText.length !== 10) return 'Not a valid mobile number';
  return cleanText;
};

export const addMask = (text: string, value: string, start: number, end?: number): string => {
  if (
    typeof text !== 'string' ||
    typeof value !== 'string' ||
    typeof start !== 'number' ||
    start < 0 ||
    start > text.length
  ) {
    return 'not valid input';
  }

  if ((end === undefined || end === null) && start >= 0 && start <= text.length) {
    return `${text.slice(0, start)}${value.repeat(text.length - start)}`;
  }

  if (
    typeof end !== 'number' ||
    end === undefined ||
    end === null ||
    end < start ||
    end > text.length ||
    end < 0
  ) {
    return 'end is invalid';
  }

  return `${text.slice(0, start)}${value.repeat(end - start)}${text.slice(end, text.length)}`;
};

export const removeBase64Prefix = (base64String: string) => {
  const commaIndex = base64String.indexOf(',');

  if (commaIndex !== -1) {
    // Remove the prefix including the comma
    return base64String.slice(commaIndex + 1);
  }
  return base64String;
};
